"use client";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { auth } from "@/lib/api";

function signupErrorMessage(err: unknown) {
  const e = err as {
    code?: string;
    response?: { status?: number; data?: { detail?: string } };
  };
  const detail = e?.response?.data?.detail || "";
  if (detail.toLowerCase().includes("email already registered")) return "existing_account";
  if (e?.code === "SESSION_NOT_RETAINED" || e?.code === "SESSION_COOKIE_BLOCKED") {
    return "Account created, but your browser did not keep the session. Please sign in to continue.";
  }
  if (e?.code === "ECONNABORTED") return "We’re having trouble connecting. Please try again in a moment.";
  if (e?.code === "ERR_NETWORK" || !e?.response) return "Couldn’t connect to OpenHomesAI. Check your connection and try again.";
  if (e.response.status === 429) return "Too many signup attempts. Wait a minute and try again.";
  if (e.response.status && e.response.status >= 500) return "OpenHomesAI signup is temporarily unavailable. Please try again shortly.";
  return detail || "Could not create your account. Please try again.";
}

export default function SignupPage() {
  const [mounted, setMounted] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [invitedParam, setInvitedParam] = useState(false);
  const [betaMessage, setBetaMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [targetTowns, setTargetTowns] = useState("");
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingAccountEmail, setExistingAccountEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    // Prefill email when arriving from a lead-capture CTA (e.g. anonymous realtor request).
    const prefillEmail = params.get("email");
    if (prefillEmail) setEmail(prefillEmail);
    // invited=1 arrives from the beta invite email. It only switches WHICH form
    // renders (account instead of waitlist) — the backend signup gate (waitlist
    // status == "invited") is the enforcer, so a fabricated param just yields the
    // friendly private-beta 403 in the error box below.
    setInvitedParam(params.get("invited") === "1");
    auth.betaStatus()
      .then((response) => {
        setInviteOnly(Boolean(response.data.invite_only));
        setBetaMessage(response.data.message || "");
      })
      .catch(() => {
        setInviteOnly(false);
      });
  }, []);
  if (!mounted) return null;

  const redirectTo = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "").get("redirect") || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setExistingAccountEmail("");
    setLoading(true);
    try {
      await signup(name, email, password);
      // If the user came from a specific action (e.g. "save this search"), return
      // them there. Otherwise send through onboarding.
      router.push(redirectTo || "/onboarding");
    } catch (err: unknown) {
      const message = signupErrorMessage(err);
      if (message === "existing_account") {
        setExistingAccountEmail(email.trim());
        setError("");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const response = await auth.joinWaitlist({
        name,
        email,
        target_towns: targetTowns,
        note,
      });
      setSuccess(response.data.message || "You’re on the list. We’ll send access shortly.");
    } catch (err: unknown) {
      const e = err as { code?: string; response?: { status?: number; data?: { detail?: string } } };
      if (e?.code === "ECONNABORTED") setError("We’re having trouble connecting. Please try again in a moment.");
      else if (e?.code === "ERR_NETWORK" || !e?.response) setError("Couldn’t connect to OpenHomesAI. Check your connection and try again.");
      else if (e.response.status === 429) setError("Too many requests. Wait a minute and try again.");
      else setError(e.response.data?.detail || "Could not join the beta list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const encodedExistingEmail = encodeURIComponent(existingAccountEmail);
  const encodedRedirect = redirectTo ? `&redirect=${encodeURIComponent(redirectTo)}` : "";
  // Invitees follow their email link (invited=1) into the ACCOUNT form even while
  // the beta is invite-only; everyone else in invite-only mode sees the waitlist form.
  const showAccountForm = !inviteOnly || invitedParam;

  return (
    <div className="auth-page-shell">
      <div className="auth-card auth-card-compact">
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--color-ink)", margin: "0 0 6px", letterSpacing: "-0.01em" }}>Create your account</h1>
        <p style={{ fontSize: 14, color: "var(--color-muted-3)", margin: "0 0 28px" }}>
          {!showAccountForm
            ? (betaMessage || "OpenHomesAI is in private beta. Join the list and we’ll send access shortly.")
            : inviteOnly
              ? "You’re invited — create your account to get started."
              : "Save searches and get alerts for new matches"}
        </p>

        {error && (
          <div style={{ background: "var(--color-error-bg)", border: "1px solid var(--color-error-border)", borderRadius: 8, padding: "10px 14px", marginBottom: 20, fontSize: 13, color: "var(--color-error-text)" }}>
            {error}
          </div>
        )}

        {existingAccountEmail && (
          <div style={{ background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
            <p style={{ fontSize: 14, color: "var(--color-warning-text)", fontWeight: 700, margin: "0 0 4px" }}>
              That email already has an account.
            </p>
            <p style={{ fontSize: 13, color: "var(--color-warning-text)", lineHeight: 1.5, margin: "0 0 12px" }}>
              Sign in with {existingAccountEmail}, or reset your password if you do not remember it.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link href={`/auth/login?email=${encodedExistingEmail}${encodedRedirect}`} style={{ border: "1px solid var(--color-brand-dark)", background: "var(--color-brand-dark)", color: "var(--color-surface)", borderRadius: 8, padding: "8px 11px", fontSize: 12, fontWeight: 780, textDecoration: "none" }}>
                Sign in
              </Link>
              <Link href={`/auth/forgot-password?email=${encodedExistingEmail}`} style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-brand-dark)", borderRadius: 8, padding: "8px 11px", fontSize: 12, fontWeight: 780, textDecoration: "none" }}>
                Forgot password
              </Link>
            </div>
          </div>
        )}

        {success && (
          <div role="status" style={{ background: "var(--color-primary-soft)", border: "1px solid var(--color-brand)", borderRadius: 10, padding: "11px 13px", marginBottom: 18, fontSize: 13, color: "var(--color-brand-dark)", lineHeight: 1.45, fontWeight: 650 }}>
            {success}
          </div>
        )}

        {!showAccountForm ? (
        <form onSubmit={handleWaitlistSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required className="auth-input" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="auth-input" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Target towns</label>
            <input type="text" value={targetTowns} onChange={e => setTargetTowns(e.target.value)} className="auth-input" placeholder="Chatham, Madison, Morristown" />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>What are you looking for?</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} className="auth-input" rows={4} style={{ resize: "vertical", minHeight: 96 }} placeholder="Budget, beds, commute, schools, or anything else helpful." />
          </div>
          <button type="submit" disabled={loading} className="auth-submit">
            {loading ? "Joining…" : "Join beta list"}
          </button>
        </form>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="auth-input" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-muted)", marginBottom: 6 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                className="auth-input"
                style={{ paddingRight: 44 }}
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-muted-3)", fontSize: 13, fontFamily: "inherit", padding: 0, lineHeight: 1 }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p style={{ fontSize: 12, color: "var(--color-muted-3)", margin: "6px 0 0" }}>At least 8 characters</p>
          </div>
          <button type="submit" disabled={loading} className="auth-submit">
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
        )}

        <p style={{ fontSize: 13, color: "var(--color-muted-3)", textAlign: "center", marginTop: 20 }}>
          {!showAccountForm ? "Already have beta access?" : "Already have an account?"}{" "}
          <Link href={`/auth/login${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`} style={{ color: "var(--color-brand)", fontWeight: 500 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
